import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { clearAdminApiPassword } from '../../services/http';

const AuthContext = createContext(null);

const STORAGE_KEY = 'jet_user';

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setUser(readStoredUser());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback((nextUser) => {
    setUser(nextUser);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
    } catch (_) { /* ignore */ }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    clearAdminApiPassword();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* ignore */ }
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    user,
    isLoggedIn: Boolean(user?.isLoggedIn),
    login,
    logout,
    updateUser,
  }), [user, login, logout, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Alias for session helpers (same as useAuth). */
export const useSession = useAuth;
